import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { api } from "./api";
import { stopGoogleAutoSelect } from "./googleOneTap";

const AuthCtx = createContext(null);

/**
 * "This browser has signed in to NAVI CARE before" hint. Only the e-mail, first name and sign-in
 * method are stored (never a password or token). It survives log out on purpose, so the landing page
 * can offer a one-click Log in to returning customers.
 */
export const RETURNING_KEY = "nw_returning";
export function readReturningHint() {
  try { return JSON.parse(localStorage.getItem(RETURNING_KEY) || "null"); } catch { return null; }
}
// "identifier" is what the PIN sign-in needs: the mobile number when the account has one, otherwise the e-mail (staff).
export function forgetReturning() { try { localStorage.removeItem(RETURNING_KEY); } catch {} }
function rememberReturning(u, method) {
  if (!u?.email && !u?.phone) return;
  // Staff and admins keep their own sign-in; the personalised customer greeting is for customers only.
  const role = String(u.role || (u.is_admin ? "admin" : "customer")).toLowerCase();
  try {
    localStorage.setItem(RETURNING_KEY, JSON.stringify({
      email: u.email || "",
      phone: u.phone || "",
      identifier: u.phone || u.email,
      name: String(u.name || "").trim().split(/\s+/)[0],
      role,
      method: method || (u.provider === "google" || u.google_id ? "google" : "pin"),
    }));
  } catch {}
}

export function AuthProvider({ children }) {
  const cachedUser = (() => { try { return JSON.parse(localStorage.getItem("nw_user") || "null"); } catch { return null; } })();
  const [user, setUser] = useState(cachedUser);
  const [loading, setLoading] = useState(!cachedUser);

  // Prevent an old /auth/me request from overwriting a newer login.
  const authVersion = useRef(0);

  const refresh = useCallback(async () => {
    const requestVersion = authVersion.current;

    // Always check /auth/me — auth can come from a Bearer token (email/
    // password login) OR from an httpOnly session cookie (Google sign-in).
    // We can't tell which one is active just by looking at localStorage,
    // so we let the backend decide.
    try {
      const { data } = await api.get("/auth/me", { timeout: 8000 });

      // Only accept this response if no newer login/logout happened. A stale
      // /auth/me response must never overwrite the token from a successful login.
      if (requestVersion === authVersion.current) {
        if (data?.token) localStorage.setItem("nw_token", data.token);
        const { token: _token, ...safeUser } = data || {};
        setUser(safeUser);
        try { localStorage.setItem("nw_user", JSON.stringify(safeUser)); } catch {}
        rememberReturning(safeUser);
      }
    } catch (error) {
      // Only clear a session on an explicit authentication failure. A temporary
      // network/server error must not erase a valid remembered sign-in on PWA launch.
      if (requestVersion === authVersion.current) {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem("nw_token");
          localStorage.removeItem("nw_user");
          setUser(null);
        } else {
          try { setUser(JSON.parse(localStorage.getItem("nw_user") || "null")); } catch { /* keep current state */ }
        }
      }
    } finally {
      if (requestVersion === authVersion.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Password sign-in exists for ADMIN accounts only (the server refuses it for everyone else).
  const login = async (identifier, password) => {
    const id = String(identifier || "").trim();
    const { data } = await api.post("/auth/login", { identifier: id.includes("@") ? id.toLowerCase() : id, password });

    if (!data?.token) {
      throw new Error("Login did not return a login token");
    }

    authVersion.current += 1;
    try { sessionStorage.removeItem("nw_logged_out"); } catch {}

    localStorage.setItem("nw_token", data.token);

    setUser(data.user);
    try { localStorage.setItem("nw_user", JSON.stringify(data.user)); } catch {}
    rememberReturning(data.user, "password");
    setLoading(false);

    return data.user;
  };

  // Step 1 of sign-in: who does this mobile number / e-mail belong to? (first name + masked contact only)
  const lookupAccount = async (identifier) => {
    const id = String(identifier || "").trim();
    const { data } = await api.post("/auth/lookup", { identifier: id.includes("@") ? id.toLowerCase() : id });
    return data;
  };

  // PIN sign-in. `identifier` is a mobile number or an e-mail.
  const loginPin = async (identifier, pin) => {
    const id = String(identifier || "").trim();
    const { data } = await api.post("/auth/login-pin", { identifier: id.includes("@") ? id.toLowerCase() : id, pin });
    if (!data?.token) throw new Error("Login did not return a login token");
    authVersion.current += 1;
    try { sessionStorage.removeItem("nw_logged_out"); } catch {}
    localStorage.setItem("nw_token", data.token);
    setUser(data.user);
    try { localStorage.setItem("nw_user", JSON.stringify(data.user)); } catch {}
    try { localStorage.setItem("nw_saved_email", data.user?.email || ""); } catch {}
    rememberReturning(data.user, "pin");
    setLoading(false);
    return data.user;
  };

  const migratePin = async (email, current_password, pin) => {
    const { data } = await api.post("/auth/pin/migrate", { email, current_password, pin });
    if (!data?.token) throw new Error("PIN setup did not return a login token");
    authVersion.current += 1;
    try { sessionStorage.removeItem("nw_logged_out"); } catch {}
    localStorage.setItem("nw_token", data.token);
    setUser(data.user);
    try { localStorage.setItem("nw_user", JSON.stringify(data.user)); localStorage.setItem("nw_saved_email", data.user?.email || email); } catch {}
    rememberReturning(data.user, "pin"); setLoading(false); return data.user;
  };

  // New customer: name + e-mail + mobile number + 4-digit PIN. A 409 {code:"phone_exists"} means the mobile already has an account.
  const register = async (name, email, phone, pin, referral_code = "", attribution = {}) => {
    const { data } = await api.post("/auth/register", {
      name,
      email,
      phone,
      pin,
      referral_code,
      qr_code: attribution?.qr_code || "",
      campaign_code: attribution?.campaign_code || "",
    });

    if (!data?.token) {
      throw new Error("Account creation did not return a login token");
    }

    authVersion.current += 1;
    try { sessionStorage.removeItem("nw_logged_out"); } catch {}

    localStorage.setItem("nw_token", data.token);

    setUser(data.user);
    try { localStorage.setItem("nw_user", JSON.stringify(data.user)); } catch {}
    rememberReturning(data.user, "pin");
    setLoading(false);

    return data.user;
  };

  const googleLogin = async (credential, attribution = {}) => {
    if (!credential) {
      throw new Error("Google credential was not received");
    }

    const { data } = await api.post("/auth/google", {
      credential,
      qr_code: attribution?.qr_code || "",
      campaign_code: attribution?.campaign_code || "",
    });

    authVersion.current += 1;
    try { sessionStorage.removeItem("nw_logged_out"); } catch {}

    // Google login now returns a JWT in addition to the session cookie.
    // Persisting it makes every subsequent admin upload/API call use the
    // same reliable Bearer authentication path as email/password login.
    if (data?.token) {
      localStorage.setItem("nw_token", data.token);
    }

    setUser(data.user);
    try { localStorage.setItem("nw_user", JSON.stringify(data.user)); } catch {}
    rememberReturning(data.user, "google");
    setLoading(false);

    return data.user;
  };

  // Email-code sign-in (the BACKUP way in, for a forgotten PIN). It only signs in an existing account.
  const requestOtp = async (identifier) => {
    const id = String(identifier || "").trim();
    const { data } = await api.post("/auth/otp/request", { identifier: id.includes("@") ? id.toLowerCase() : id });
    return data;
  };

  const verifyOtp = async (identifier, code) => {
    const id = String(identifier || "").trim();
    const { data } = await api.post("/auth/otp/verify", { identifier: id.includes("@") ? id.toLowerCase() : id, code: String(code || "").replace(/\D/g, "") });
    if (!data?.token) throw new Error("Sign-in did not return a login token");
    authVersion.current += 1;
    try { sessionStorage.removeItem("nw_logged_out"); } catch {}
    localStorage.setItem("nw_token", data.token);
    setUser(data.user);
    try { localStorage.setItem("nw_user", JSON.stringify(data.user)); localStorage.setItem("nw_saved_email", data.user?.email || ""); } catch {}
    rememberReturning(data.user, "otp");
    setLoading(false);
    return { user: data.user, hasPin: !!data.has_pin, setupToken: data.setup_token || "" };
  };

  // Save a new 4-digit PIN for the signed-in account (right after an e-mail-code sign-in, using the one-time setup token).
  const savePin = async (pin, setupToken) => {
    const { data } = await api.post("/auth/pin/setup", { pin, setup_token: setupToken || undefined });
    return data;
  };

  const logout = async () => {
    authVersion.current += 1;

    try {
      await api.post("/auth/logout");
    } catch {}

    // Tell <Protected> this was a deliberate log out, so it sends the person to the
    // landing page instead of bouncing them to the login screen.
    try { sessionStorage.setItem("nw_logged_out", "1"); } catch {}
    // A deliberate log out must not be undone by Google silently signing the person back in.
    stopGoogleAutoSelect();
    localStorage.removeItem("nw_token");
    // the cached dashboard snapshot belongs to the person who just left
    try { Object.keys(localStorage).filter((k) => k.startsWith("nw_dash_")).forEach((k) => localStorage.removeItem(k)); } catch {}
    setUser(null);
    localStorage.removeItem("nw_user");
    setLoading(false);
  };

  return (
    <AuthCtx.Provider
      value={{
        user,
        loading,
        login,
        lookupAccount,
        loginPin,
        requestOtp,
        verifyOtp,
        savePin,
        migratePin,
        register,
        googleLogin,
        logout,
        refresh,
        setUser,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
